import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { Filter, FilterOption } from '@model/iceberg/service/booking';
import { Amount, RoundingDirection } from '@model/common';
import { Slider } from '@components/filters/v2/filters/generic/slider/Slider';
import { mixins } from '@styles/mixins';
import { useI18NextContext } from '@components/hooks';

const Container = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.medium
}));

const FilterHeading = styled.h5(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.default as any),
  margin: `0 0 ${theme.custom.spacing.xSmall}px 0`
}));

const SmallLabel = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any),
  color: theme.custom.colors.group4.base,
  fontWeight: 500
}));

const SelectedTimeRangeLabelFrom = styled(SmallLabel)(({ theme }: ThemeProps) => ({
  marginRight: theme.custom.spacing.xSmall
}));

const SelectedTimeRangeLabelTo = styled(SmallLabel)(({ theme }: ThemeProps) => ({
  marginLeft: theme.custom.spacing.xSmall
}));

const PriceRangePreselect = styled.div(({ theme }: ThemeProps) => ({
  paddingTop: theme.custom.spacing.xSmall,
  display: 'flex',
  flexWrap: 'wrap',
  ['span']: {
    ...(theme.custom.typography.labels.attribute as any),
    paddingRight: theme.custom.spacing.xSmall,
    fontWeight: 500
  },
  ['span:last-of-type']: {
    paddingRight: 0
  }
}));

const PreselectTitle = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any),
  paddingRight: theme.custom.spacing.xSmall,
  fontWeight: 500
}));

const PriceRangeSelect = styled.span(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any),
  ...mixins(theme).link,
  cursor: 'pointer',
  fontWeight: 500
}));

const Reset = styled(PriceRangeSelect)({
  float: 'right'
});

export interface PriceFilterProps {
  theme?: Theme;
  testId?: string;
  filter: Filter;
  onChange: (filter: Filter) => void;
}

interface PriceGroup {
  minPrice: number;
  maxPrice: number;
}

export const getPriceGroups: (minPrice: number, maxPrice: number) => Array<PriceGroup> = (
  minPrice: number,
  maxPrice: number
) => {
  if (minPrice > maxPrice || minPrice < 0 || maxPrice < 0 || isNaN(minPrice) || isNaN(maxPrice)) {
    return [];
  }
  const min: Amount = new Amount(minPrice);
  const max: Amount = new Amount(maxPrice);
  const priceDifference = max.subtract(min.value);
  const minBounds: Amount = priceDifference.divide(3).round(RoundingDirection.DOWN);
  const upperBounds: number = max.subtract(minBounds.value).round(RoundingDirection.UP).value;
  return [
    {
      minPrice: min.value,
      maxPrice: minBounds.add(min.value).value
    },
    {
      minPrice: minBounds.add(min.value).value,
      maxPrice: upperBounds
    },
    {
      minPrice: upperBounds,
      maxPrice: max.value
    }
  ];
};

export const PriceFilterComponent = ({ filter, onChange, testId }: PriceFilterProps) => {
  const t: any = useI18NextContext();
  const min: FilterOption = filter.values[0];
  const max: FilterOption = filter.values[1];
  if (!min || !max) {
    return null;
  }
  const minValue: Amount = new Amount(min.value);
  const maxValue: Amount = new Amount(max.value);
  const [selectedRange, setSelectedRange] = useState<Array<number>>([
    min.selected ? min.selected : min.value,
    max.selected ? max.selected : max.value
  ]);

  const handleClearFilter = () => {
    setSelectedRange([min.value, max.value]);
    onChange({
      ...filter,
      values: [
        {
          ...min,
          selected: min.value
        },
        {
          ...max,
          selected: max.value
        }
      ]
    });
  };

  const handleChange = (_: any, newValue: any) => setSelectedRange(newValue);
  const handleOnChangeCommitted = (_: any, newValue: any) => {
    onChange({
      ...filter,
      values: [
        {
          ...min,
          selected: newValue[0]
        },
        {
          ...max,
          selected: newValue[1]
        }
      ]
    });
  };
  const handleOnPriceRangeChange = (minPrice: number, maxPrice: number) => {
    setSelectedRange([minPrice, maxPrice]);
    onChange({
      ...filter,
      values: [
        {
          ...min,
          selected: minPrice
        },
        {
          ...max,
          selected: maxPrice
        }
      ]
    });
  };
  useEffect(() => {
    if (!min.selected && !max.selected) {
      setSelectedRange([minValue.value, maxValue.value]);
    } else if (!min.selected) {
      setSelectedRange([minValue.value, max.selected as any]);
    } else if (!max.selected) {
      setSelectedRange([min.selected as any, maxValue.value]);
    }
  }, [min.selected, max.selected]);
  const renderPriceRanges = (priceGroups: Array<PriceGroup>) =>
    priceGroups.map((priceGroup: PriceGroup, index: number) => (
      <PriceRangeSelect
        data-testid={TestId.filters.priceRangePreselectGroup}
        key={`price-range-${index}`}
        onClick={() => handleOnPriceRangeChange(priceGroup.minPrice, priceGroup.maxPrice)}
      >
        {new Amount(priceGroup.minPrice).format()} - {new Amount(priceGroup.maxPrice).format()}
        {index < priceGroups.length - 1 && ', '}
      </PriceRangeSelect>
    ));
  const showReset = filter.values.some((f) => f.selected && f.selected !== f.value);
  return (
    <Container data-testid={testId || TestId.filters.rangeSlider.main}>
      <FilterHeading data-testid={TestId.filters.rangeSlider.heading}>{filter.formattedName}</FilterHeading>
      <SelectedTimeRangeLabelFrom>{new Amount(selectedRange[0]).format()}</SelectedTimeRangeLabelFrom>-
      <SelectedTimeRangeLabelTo>{new Amount(selectedRange[1]).format()}</SelectedTimeRangeLabelTo>
      {showReset && <Reset onClick={handleClearFilter}>(reset)</Reset>}
      <Slider
        value={selectedRange}
        onChange={handleChange}
        onChangeCommitted={handleOnChangeCommitted}
        valueLabelDisplay="auto"
        min={minValue.value}
        max={maxValue.value}
        step={1000}
        valueLabelFormat={(value: number) => new Amount(value).format()}
      />
      <PriceRangePreselect data-testid={TestId.filters.priceRangePreselect}>
        <PreselectTitle>{t('filters__label--select')}:</PreselectTitle>
        {renderPriceRanges(getPriceGroups(minValue.value, maxValue.value))}
      </PriceRangePreselect>
    </Container>
  );
};

export const PriceFilter = withTheme(PriceFilterComponent);
